import React, { useEffect, useState, useMemo } from "react";
import { compose } from "redux";
import { Field, reduxForm, reset, SubmissionError } from "redux-form";
import I from "immutable";

import injectSheet from "lib/sheet";
import { actions } from "domain/customer";
import { connect } from "cpcs-reconnect";

import { token } from "domain/env";
import Input from "components/form/element/Input";
import Password from "components/form/element/Password";
import Select from "components/form/element/Select";
import rules from "components/form/validation";
import CloseIcon from "./close.svg";
import Api from "domain/api";
import { selectors as enterpriseSelectors } from "domain/enterprise";
import { getSeatTypesFromStorage } from "utils/seatTypes";

const enterpriseTypes = [
  { id: "Enterprise", title: "Enterprise" },
  { id: "Chargeable", title: "Chargeable" },
  { id: "Internal", title: "Internal" },
  { id: "Custom", title: "Custom" },
];

const onSubmit = async (formData, dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch({
      type: actions.registerUserAction.type,
      payload: {
        privacyTermsAccept: true,
        enterprise: formData.enterprise,
        country: formData.country,
        email: formData.email,
        seatTypeId: formData.seatTypeId,
        password: formData.password,
        lastname: formData.lastName,
        firstname: formData.firstName,
        resolve: (response) => {
          dispatch(reset("customerSignUp"));
          resolve({
            success: true,
            data: response,
          });
        },
        reject: (error) => {
          reject(
            new SubmissionError({
              _error: error.response?.data?.message || "Registration failed",
            })
          );
        },
      },
    });
  });
};

const CustomerSignUpForm = ({
  classes,
  handleSubmit,
  submitting,
  onCancel,
  invalid,
  enterprises,
  token,
}) => {
  const [externalCountries, setExternalCountries] = useState([]);
  const [loadingExternalCountries, setLoadingExternalCountries] =
    useState(true);
  const countriesArray = externalCountries.length > 0 ? externalCountries : [];

  const seatTypes = useMemo(
    () =>
      getSeatTypesFromStorage().map((st) => ({
        id: st?.id,
        title: st?.description,
      })),
    []
  );

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoadingExternalCountries(true);
        const response = await Api.getExternalCountries({ token });
        const data = await response.data;

        const transformedCountries = data.content.map((country) => ({
          id: country.id,
          title: country.title,
          code: country.code,
          historical: country.historical,
        }));
        setExternalCountries(transformedCountries);
      } catch (error) {
        console.error("Failed to fetch countries:", error);
      } finally {
        setLoadingExternalCountries(false);
      }
    };

    fetchCountries();
  }, [token]);

  const enterprisesArray = enterprises
    ? enterprises.toJS().map((enterprise) => ({
        id: enterprise.id,
        title: enterprise.enterpriseName,
      }))
    : [];

  const handleFormSubmit = (formData) => {
    return handleSubmit(formData).then((result) => {
      if (result && result.success) {
        setTimeout(() => {
          onCancel();
        }, 1000);
      }
    });
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h2 className={classes.title}>Add New User</h2>
        <button
          type="button"
          className={classes.closeButton}
          onClick={onCancel}
          aria-label="Close"
        >
          <img src={CloseIcon} alt="Close" className={classes.closeIcon} />
        </button>
      </div>
      <div className={classes.scrollableContent}>
        <form
          id="customerSignUpForm"
          onSubmit={handleFormSubmit}
          className={classes.form}
        >
          <div className={classes.fieldGroup}>
            <div className={classes.fieldRow}>
              <div className={classes.labelContainer}>
                <label className={classes.label}>
                  First Name <span className={classes.required}>*</span>
                </label>
              </div>
              <Field
                name="firstName"
                placeholder="Enter first name"
                component={Input}
                validate={[rules.required]}
                className={classes.input}
              />
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.labelContainer}>
                <label className={classes.label}>
                  Last Name <span className={classes.required}>*</span>
                </label>
              </div>
              <Field
                name="lastName"
                placeholder="Enter last name"
                component={Input}
                validate={[rules.required]}
                className={classes.input}
              />
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.labelContainer}>
                <label className={classes.label}>
                  Email <span className={classes.required}>*</span>
                </label>
              </div>
              <Field
                name="email"
                placeholder="Enter email"
                component={Input}
                validate={[rules.required, rules.email]}
                className={classes.input}
                autoComplete="off"
              />
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.labelContainer}>
                <label className={classes.label}>
                  Password <span className={classes.required}>*</span>
                </label>
              </div>
              <Field
                name="password"
                placeholder="Enter password"
                component={Password}
                validate={[rules.required, rules.passwordLength]}
                className={classes.input}
                autoComplete="new-password"
              />
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.labelContainer}>
                <label className={classes.label}>
                  Country <span className={classes.required}>*</span>
                </label>
              </div>
              <Field
                name="country"
                placeholder="Select Country"
                component={Select}
                list={I.fromJS(countriesArray)}
                disabled={loadingExternalCountries}
                parseValue={(v) => (v && v.get ? v.get("id") : "")}
                isClearable={false}
                validate={[rules.required]}
                className={classes.select}
              />
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.labelContainer}>
                <label className={classes.label}>
                  Config <span className={classes.required}>*</span>
                </label>
              </div>
              <Field
                name="config"
                placeholder="Select Enterprise"
                component={Select}
                list={I.fromJS(enterpriseTypes)}
                parseValue={(v) => (v && v.get ? v.get("id") : "")}
                isClearable={false}
                validate={[rules.required]}
                className={classes.select}
              />
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.labelContainer}>
                <label className={classes.label}>
                  Seat Type <span className={classes.required}>*</span>
                </label>
              </div>
              <Field
                name="seatTypeId"
                placeholder="Select Seat Type"
                component={Select}
                list={I.fromJS(seatTypes)}
                parseValue={(v) => (v && v.get ? v.get("id") : "")}
                validate={[rules.required]}
                className={classes.select}
              />
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.labelContainer}>
                <label className={classes.label}>Enterprise</label>
              </div>
              <Field
                name="enterprise"
                placeholder="Select Enterprise"
                component={Select}
                list={I.fromJS(enterprisesArray)}
                parseValue={(v) => (v && v.get ? v.get("id") : "")}
                isClearable={true}
                className={classes.select}
                validate={[rules.required]}
              />
            </div>
          </div>
        </form>
      </div>
      <div className={classes.buttonRow}>
        <button
          type="button"
          className={classes.cancelButton}
          onClick={onCancel}
        >
          CANCEL
        </button>
        <button
          type="submit"
          className={classes.saveButton}
          disabled={submitting || invalid}
          form="customerSignUpForm"
        >
          {submitting ? "SAVING..." : "SAVE"}
        </button>
      </div>
    </div>
  );
};

const sheet = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ffffff",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: [30, 30, 20, 30],
    borderBottom: "1px solid #b45309",
    flexShrink: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    color: "#1f2937",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    padding: 8,
    cursor: "pointer",
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#f3f4f6",
    },
    "&:active": {
      backgroundColor: "#e5e7eb",
    },
  },
  closeIcon: {
    width: 24,
    height: 24,
  },
  scrollableContent: {
    flex: 1,
    padding: [20, 30, 0, 30],
  },
  form: {
    height: "100%",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    paddingBottom: 20,
    marginTop: 32,
  },
  fieldRow: {
    display: "flex",
    gap: "8px",
    "& > div": {
      marginBottom: "0px",
    },
  },
  label: {
    fontSize: 16,
    fontWeight: 500,
    color: "#374151",
    marginBottom: 4,
  },
  required: {
    color: "#ef4444",
  },
  input: {
    minWidth: "300px",
    marginLeft: "2px",
    height: 44,
    padding: [0, 12],
    border: "1px solid #d1d5db",
    borderRadius: 6,
    fontSize: 14,
    marginBottom: 0,
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  password: {
    width: "100%",
    marginLeft: "2px",
    height: 44,
    padding: [0, 12],
    border: "1px solid #d1d5db",
    borderRadius: 6,
    fontSize: 14,
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  select: {
    minWidth: "300px",
    border: "1px solid #d1d5db !important",
    borderRadius: "6px !important",
    "& > div": {
      minHeight: 44,
      borderRadius: "6px",
      "& > div": {
        minHeight: 44,
        padding: [0, 12],
      },
    },
  },
  buttonRow: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    padding: [20, 30, 30, 30],
    flexShrink: 0,
  },
  cancelButton: {
    width: 150,
    padding: [12, 24],
    backgroundColor: "transparent",
    color: "#6b7280",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f9fafb",
      borderColor: "#9ca3af",
    },
  },
  saveButton: {
    width: 150,
    padding: [12, 24],
    backgroundColor: "#b45309",
    color: "white",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#92400e",
    },
    "&:disabled": {
      backgroundColor: "#d1d5db",
      cursor: "not-allowed",
    },
  },
  labelContainer: {
    width: "50%",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
};

const getInitialSeatType = () => {
  const seatTypes = getSeatTypesFromStorage();
  return seatTypes.length > 0 ? seatTypes[0].id : null;
};

export default compose(
  connect({
    enterprises: enterpriseSelectors.list,
    token,
  }),
  injectSheet(sheet),
  reduxForm({
    form: "customerSignUp",
    onSubmit,
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      country: 244,
      config: "Enterprise",
      seatTypeId: getInitialSeatType(),
      enterprise: 81,
    },
  })
)(CustomerSignUpForm);
